'use client';

/*
  NOTE: This component used to have a separate wrapper, PoofyRainbowButton, that added a glassy effect to the button. I was using that in all but 1 of the places I used the ButtonRainbow, so I merged them. This component includes the PoofyRainbowButton styling.
*/
import * as React from 'react';
import { styled } from '@linaria/react';

import useBrowser from '@/hooks/use-browser';

import ButtonPoofy from '@/components/ButtonPoofy';

import useRainbow from './use-rainbow.hook';

interface Props
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  intervalDelay?: number;
}

function ButtonRainbow({
  children,
  intervalDelay = 1300,
  className,
  style = {},
  ...delegated
}: Props) {
  const transitionDelay = intervalDelay * 1.25;

  const colors = useRainbow({ intervalDelay });

  // Frustratingly, the transition doesn't work in Firefox, despite the APIs being supported according to caniuse. I suspect maybe this is a bug in the latest Firefox release, since this isn't a regression between old blog and new.
  // For now, we’ll disable this in Firefox. In 2025, I should see if I can remove this:
  const browser = useBrowser();

  const colorKeys = Object.keys(colors);

  return (
    <Button
      {...delegated}
      className={className}
      data-disable-transition={browser === 'firefox'}
      style={{
        ...style,
        ...colors,
        transition: `
          ${colorKeys[0]} ${transitionDelay}ms linear,
          ${colorKeys[1]} ${transitionDelay}ms linear,
          ${colorKeys[2]} ${transitionDelay}ms linear
        `,
        '--background': `
          radial-gradient(
            circle at top left,
            var(${colorKeys[2]}),
            var(${colorKeys[1]}),
            var(${colorKeys[0]})
          )
        `,
      }}
    >
      {children}
    </Button>
  );
}

const Button = styled(ButtonPoofy)`
  min-height: 3.75rem;
  backface-visibility: hidden;
  background: var(--background);

  &[data-disable-transition='true'] {
    background: hsl(240deg, 80%, 40%);
  }
`;

export default ButtonRainbow;
